export const formatBenchmarkValue = (value, unit) => {
  if (unit === 'percent') {
    return `${value}%`
  }

  if (unit === 'hours') {
    return `${value} hr`
  }

  if (unit === 'currency') {
    return `$${value}`
  }

  return String(value)
}

export const BENCHMARK_STATUS = {
  TOP_10: 'top10',
  ABOVE_MEDIAN: 'aboveMedian',
  AT_MEDIAN: 'atMedian',
  BELOW_MEDIAN: 'belowMedian',
}

export const isAboveMedian = ({ coachValue, medianValue, higherIsBetter }) =>
  higherIsBetter ? coachValue >= medianValue : coachValue <= medianValue

export const isTop10 = ({ coachValue, top10Value, higherIsBetter }) =>
  higherIsBetter ? coachValue >= top10Value : coachValue <= top10Value

export const isAtMedian = ({ coachValue, medianValue }) =>
  coachValue === medianValue

export const getBenchmarkStatusTier = metric => {
  if (isTop10(metric)) {
    return BENCHMARK_STATUS.TOP_10
  }

  if (isAtMedian(metric)) {
    return BENCHMARK_STATUS.AT_MEDIAN
  }

  if (isAboveMedian(metric)) {
    return BENCHMARK_STATUS.ABOVE_MEDIAN
  }

  return BENCHMARK_STATUS.BELOW_MEDIAN
}

export const getBenchmarkStatusLabel = tier => {
  switch (tier) {
    case BENCHMARK_STATUS.TOP_10:
      return 'Top 10%'
    case BENCHMARK_STATUS.AT_MEDIAN:
      return 'At median'
    case BENCHMARK_STATUS.ABOVE_MEDIAN:
      return 'Above median'
    default:
      return 'Below median'
  }
}

export const getBarScale = ({ coachValue, medianValue }) =>
  Math.max(coachValue, medianValue, 1) * 1.15

export const getBarFillPercent = ({ coachValue, medianValue }) => {
  const scale = getBarScale({ coachValue, medianValue })
  return Math.min(100, (coachValue / scale) * 100)
}

export const getMedianMarkerPercent = ({ coachValue, medianValue }) => {
  const scale = getBarScale({ coachValue, medianValue })
  return Math.min(100, (medianValue / scale) * 100)
}

export const getRelativeGap = ({ coachValue, medianValue, higherIsBetter }) => {
  if (medianValue === 0) {
    return 0
  }

  const rawGap = higherIsBetter
    ? medianValue - coachValue
    : coachValue - medianValue

  return Math.max(rawGap, 0) / medianValue
}

export const getBenchmarkSummary = metrics => {
  const aboveMedianCount = metrics.filter(isAboveMedian).length
  const belowMedianMetrics = metrics
    .filter(metric => !isAboveMedian(metric))
    .map(metric => ({
      ...metric,
      gap: getRelativeGap(metric),
    }))
    .sort((a, b) => b.gap - a.gap)

  const biggestUpside = belowMedianMetrics[0]

  return {
    aboveMedianCount,
    totalCount: metrics.length,
    biggestUpsideLabel: biggestUpside?.label ?? null,
  }
}

export const getDetailScaleRange = metric => {
  const { gettingStartedValue, top10Value, coachValue, higherIsBetter } = metric

  if (higherIsBetter) {
    const min = gettingStartedValue
    const max = Math.max(top10Value, coachValue)

    return {
      min,
      max: max === min ? max + 1 : max,
    }
  }

  const min = Math.min(top10Value, coachValue)
  const max = Math.max(gettingStartedValue, coachValue)

  return {
    min,
    max: max === min ? max + 1 : max,
  }
}

export const getDetailScalePercent = (value, metric) => {
  const { min, max } = getDetailScaleRange(metric)
  const span = max - min
  const normalized = metric.higherIsBetter
    ? (value - min) / span
    : (max - value) / span

  return Math.max(0, Math.min(100, normalized * 100))
}
